<template>
  <div class="part-edit-inspection">
    <b-button
      v-if="inspection && isInitStage"
      variant="outline-primary"
      class="mb-2 template-switch-button"
      @click="toggleTemplateSelector"
    >
      <b-icon icon="arrow-left-right" aria-hidden="true" />
    </b-button>

    <multiselect
      v-if="!inspection || showTemplateSelector"
      id="inspection"
      :custom-label="getItemTitle"
      :selectLabel="$t('Add New')"
      :multiple="false"
      :options="currentProjectActiveInspectionTemplates"
      :placeholder="inspection ? $t('Switch Template') : $t('Choose Template')"
      class="boon-multiselect mb-4"
      @input="addInspectionToPart"
    />

    <div v-if="inspection">
      <div>
        <span>{{ $t('Address') }}</span>
        <span>{{ address }}</span>
      </div>

      <div v-if="isLinkAvailable">
        <a :href="inspectionLink" target="_blank" class="mr-3">
          <b-iconstack class="link-icon">
            <b-icon icon="square" />
            <b-icon icon="arrow-up-right" scale="0.75" />
          </b-iconstack>
          <span>{{ $t('Open link') }}</span>
        </a>

        <span v-if="inspectionStageTitle">{{ inspectionStageTitle }}</span>
      </div>

      <div class="mt-4">{{ $t('Starts') }}</div>
      <input
        type="datetime-local"
        :value="datetimeLocal(inspection.beginTime)"
        :readonly="!hasSimplePermissions('saveInspectionProperties') || !isInitStage"
        class="inspection-date-time"
        @change="updateDateTimeProperty('beginTime', $event.target.value)"
      />

      <div class="mt-4">{{ $t('Ends') }}</div>
      <input
        type="datetime-local"
        :value="datetimeLocal(inspection.endTime)"
        :min="datetimeLocal(inspection.beginTime)"
        :readonly="!hasSimplePermissions('saveInspectionProperties') || !isInitStage"
        class="inspection-date-time"
        @change="updateDateTimeProperty('endTime', $event.target.value)"
      />
    </div>

    <part-edit-inspection-participants :part="part" :disabled="!isInitStage" />

    <div v-if="pdfs" class="mt-3">
      <div v-for="pdf of pdfs" v-if="pdf.url" :key="`pdf-${pdf.locale}`">
        <a :href="pdf.url" target="_blank" class="download-pdf-button mt-3">
          <img class="flag-icon mr-2" :src="require(`@/assets/img/flags/${pdf.locale}.svg`)" :alt="pdf.locale" />
          <span>{{ $t(`inspection-download-report.${pdf.locale}`) }}</span>
        </a>
      </div>
    </div>

    <div
      v-if="inspection && isInitStage"
      class="fill-width mt-4 d-flex flex-row align-items-center justify-content-center"
    >
      <b-button pill variant="primary" class="btn-sm" @click="changeStageToPrepare">
        {{ $t('Create Inspection') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import debounce from 'debounce';

import constants from '@/constants';

import Multiselect from 'vue-multiselect';
import PartEditInspectionParticipants from './PartEditInspectionParticipants';

import getTranslatedString from '@/utils/getTranslatedString';

export default {
  name: 'PartEditInspection',

  components: {
    Multiselect,
    PartEditInspectionParticipants,
  },

  props: {
    part: {
      default: () => ({}),
      type: Object,
    },
  },

  data() {
    return {
      showTemplateSelector: false,
      changinToPrepare: false,
      pdfs: [],
    };
  },

  computed: {
    ...mapGetters([
      'currentProjectActiveInspectionTemplates',
      'selectedPartInspection',
      'hasSimplePermissions',
      'currentProject',
    ]),

    inspection() {
      return this.selectedPartInspection;
    },

    inspectionLink() {
      return this.inspection && this.inspection.token
        ? `${window.location.origin}/inspection/${this.inspection.token}`
        : null;
    },

    isLinkAvailable() {
      return this.inspection && this.inspection.availableOnBackend && !this.isInitStage && this.inspectionLink;
    },

    inspectionStage() {
      return (this.inspection && this.inspection.currentStage) || null;
    },

    isInitStage() {
      return this.inspectionStage == null || this.inspectionStage === constants.inspectionStages.init;
    },

    inspectionStageTitle() {
      return this.inspectionStage && this.$t(`inspection-stage-title.${this.inspectionStage}`);
    },

    pdfLocales() {
      const locales = [...this.$i18n.locales()];
      // if (!locales.includes('ru')) {
      //   locales.push('ru')
      // }

      return locales;
    },

    pdfFileKeys() {
      if (!this.inspection || !this.inspection.token) {
        return;
      }

      const pdfReadyStages = [constants.inspectionStages.reported, constants.inspectionStages.finalized];
      if (!pdfReadyStages.includes(this.inspectionStage)) {
        return;
      }

      const finalizedStage = this.inspection.stages && this.inspection.stages[constants.inspectionStages.finalized];
      const files = finalizedStage && finalizedStage.localizedFileData;
      if (!files) {
        return;
      }

      const fileKeys = this.pdfLocales.map((locale) => {
        const fileKey = files[locale] && files[locale].fileKey;
        return fileKey && { locale, fileKey };
      });

      return fileKeys.filter(Boolean);
    },

    address() {
      const address = this.currentProject && this.currentProject.address;
      if (!address) {
        return '';
      }

      return [address.street, [address.zipCode, address.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    },
  },

  watch: {
    pdfFileKeys: {
      immediate: true,
      handler(next, prev) {
        if (next === prev || (!next && !prev)) {
          return;
        }

        if (!next || !next.length) {
          this.pdfs = [];
          return;
        }

        this.pdfs = next.map(({ locale, fileKey }) => {
          const pdf = this.pdfs.find((pdf) => pdf.locale === locale && pdf.fileKey === fileKey);
          return pdf || { locale, fileKey, url: null, fetching: false, fetched: false };
        });
      },
    },

    pdfs: {
      immediate: true,
      handler(next) {
        next.forEach((pdf) => {
          if (!pdf.fetching && !pdf.fetched) {
            this.fetchPdfUrl(pdf.locale, pdf.fileKey);
          }
        });
      },
    },
  },

  methods: {
    ...mapActions(['addPartInspection', 'saveInspectionProperties', 'fetchInspectionPdfUrl']),

    getItemTitle({ title }) {
      return getTranslatedString(title);
    },

    datetimeLocal(date) {
      return moment(date).format('YYYY-MM-DDTHH:mm');
    },

    async updateDateTimeProperty(property, value) {
      if (this.inspection[property] === value) {
        return;
      }

      const update = {
        [property]: moment(value).toISOString(),
      };

      if (property === 'beginTime') {
        const secondsDiff = moment(value).diff(this.inspection.beginTime, 's');
        update.endTime = moment(this.inspection.endTime).add(secondsDiff, 's').toISOString();
      }

      return this.saveInspectionProperties({
        updateInspection: this.inspection,
        newInspectionProperties: update,
      });
    },

    async changeStageToPrepare() {
      if (!this.isInitStage || this.changinToPrepare) {
        return;
      }

      this.changinToPrepare = true;
      try {
        await this.saveInspectionProperties({
          updateInspection: this.inspection,
          newInspectionProperties: { currentStage: constants.inspectionStages.prepare },
        });
      } finally {
        this.changinToPrepare = false;
      }
    },

    updateSimpleProperty: debounce(function (property, newValue) {
      if (this.inspection[property] !== newValue) {
        this.saveInspectionProperties({
          updateInspection: this.inspection,
          newInspectionProperties: { [property]: newValue },
        });
      }
    }, 1400),

    addInspectionToPart(template) {
      this.addPartInspection({
        part: this.part,
        inspectionTemplateId: template._id,
      });
    },

    toggleTemplateSelector() {
      this.showTemplateSelector = !this.showTemplateSelector && this.isInitStage;
    },

    getPdf(locale, fileKey) {
      return this.pdfs.find((pdf) => pdf.locale === locale && pdf.fileKey === fileKey);
    },

    async fetchPdfUrl(locale, fileKey) {
      let pdf = this.getPdf(locale, fileKey);
      if (!pdf) {
        return;
      }

      let url = null;
      try {
        pdf.fetching = true;
        url = await this.fetchInspectionPdfUrl({ token: this.inspection && this.inspection.token, locale });
      } catch (error) {
        console.error(error);
      }

      pdf = this.getPdf(locale, fileKey);
      if (pdf) {
        pdf.fetched = true;
        pdf.fetching = false;
        if (!pdf.url && url) {
          pdf.url = url;
        }
      }
    },
  },
};
</script>

<style scoped>
.part-edit-inspection {
  position: relative;
}

.inspection-date-time:invalid {
  background: rgb(203, 102, 102);
}

.inspection-date-time {
  display: block;
  border: 1px solid #ccc;
  padding: 2px 2px;
  border-radius: 2px;
  text-align: center;
}

.template-switch-button {
  position: absolute;
  right: 6px;
  top: -48px;
  z-index: 10;
  border-radius: 50%;
  margin-bottom: 0 !important;
  padding: 6px !important;
  font-size: 0.625rem;
}

.link-icon {
  font-size: 0.875rem;
}

.download-pdf-button {
  background-color: var(--bs-primary);
  padding: 12px 16px;
  width: 100%;
  display: inline-block;
  border-radius: 5px;
  color: #ececec;
}

.download-pdf-button img {
  width: 32px;
  max-width: 32px;
  margin-right: 22px;
  display: inline-block;
  vertical-align: middle;
}

.download-pdf-button span {
  display: inline-block;
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/projectplan/PartEditInspection.vue